import React, { useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from "lucide-react";
import { pdfPreviews } from "../data/pdfPreviews";
export function SampleCarousel() {
  const [index, setIndex] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const active = pdfPreviews[index];
  const total = pdfPreviews.length;
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => triggerRef.current?.focus();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, []);
  const openDialog = () => dialogRef.current?.showModal();
  const closeDialog = () => dialogRef.current?.close();
  const go = (delta: number) => setIndex(i => (i + delta + total) % total);
  return <section className="section wash" id="preview" aria-labelledby="preview-heading" data-id="element-107" data-mp="14ndcn0yyjv7l">
      <div className="shell" data-id="element-108" data-mp="1j1dc1q0qo6ha">
        <div className="section-intro" data-id="element-109" data-mp="1j36ib47fwoor">
          <p className="eyebrow" data-id="element-110" data-mp="1j02a7vqut8st">LOOK INSIDE</p>
          <h2 id="preview-heading" data-id="element-111" data-mp="1nzwzxvyw7sca">
            Look inside your <span data-id="element-112" data-mp="1a171od0c7mmc">ready-planned workshops</span>
          </h2>
          <p data-id="element-113" data-mp="1a0ni6rv1gq67">
            Five content previews from the complete workshop guide: a timed agenda, facilitator plan,
            printable role cards, model conversation and observation sheet.
          </p>
        </div>
        <div className="sample-layout" data-id="element-114" data-mp="109apxz5bvxzn">
          <div data-id="element-115" data-mp="152m1gdl352xw">
            <div className="sample-options" role="group" aria-label="Choose a sample page" data-id="element-116" data-mp="1535i7qz01lac">
              {pdfPreviews.map((page, i) => <button key={page.id} type="button" aria-pressed={i === index} onClick={() => setIndex(i)} data-id="element-117" data-mp="qb4rjriqd9zj" data-mpi={i}>
                  <span data-id="element-118" data-mp="hem9jac1f6hq">PAGE {page.page}</span>
                  {page.label}
                  <ChevronRightIcon size={16} aria-hidden="true" data-id="element-119" data-mp="mb2yu7uz3qwe" />
                </button>)}
            </div>
            <div className="sample-explanation" aria-live="polite" data-id="element-120" data-mp="2o2uxomx66o9l">
              <h3 data-id="element-121" data-mp="2j9a2f2myo8e3">{active.heading}</h3>
              <p data-id="element-122" data-mp="2j88qkvy4fhvb">{active.text}</p>
            </div>
            <p className="small-note" data-id="element-123" data-mp="2ebs03v3g8rf2">
              These are content excerpts from the main workshop PDF. Click any preview to enlarge it
              and see the planning and materials included.
            </p>
          </div>
          <figure className="sample-figure" data-id="element-124" data-mp="36xjs4fnapgai">
            <button ref={triggerRef} type="button" onClick={openDialog} aria-haspopup="dialog" aria-label={`Enlarge actual PDF page: ${active.label}`} data-id="element-125" data-mp="3201dq9qie8v4">
              <svg className="real-pdf-page" viewBox="0 0 1125 1910" role="img" aria-label={active.alt} data-id="element-126" data-mp="2szd0vicuqejt"><image href={active.src} width="1125" height="2000" data-id="element-127" data-mp="1mkkg904awtmb" /></svg>
              <span className="zoom-label" aria-hidden="true" data-id="element-128" data-mp="1hkgbg7cr9nbx">
                <ZoomInIcon size={15} data-id="element-129" data-mp="1hn2g9z89hoqg" /> Enlarge page
              </span>
            </button>
            <div className="sample-nav" data-id="element-130" data-mp="3t5yq72x0jmt7">
              <button type="button" onClick={() => go(-1)} aria-label="Previous sample page" data-id="element-131" data-mp="20ywfgknqbxq">
                <ChevronLeftIcon size={18} aria-hidden="true" data-id="element-132" data-mp="62tyinaewtck" />
              </button>
              <span data-id="element-133" data-mp="attthmzz11cf">
                {index + 1} / {total}
              </span>
              <button type="button" onClick={() => go(1)} aria-label="Next sample page" data-id="element-134" data-mp="avwu10yr2cve">
                <ChevronRightIcon size={18} aria-hidden="true" data-id="element-135" data-mp="fv9czami2zsp" />
              </button>
            </div>
            <figcaption data-id="element-136" data-mp="ku2rn6diwwug">Preview excerpt · Page {active.page} · {active.label}</figcaption>
          </figure>
        </div>
      </div>

      <dialog ref={dialogRef} className="sample-dialog" aria-labelledby="sample-dialog-title" onClick={e => {
      if (e.target === dialogRef.current) closeDialog();
    }} data-id="element-137" data-mp="ttg324l7jvht">
        <button type="button" className="dialog-close" onClick={closeDialog} aria-label="Close preview" data-id="element-138" data-mp="2vl2lhhr1ai2o">
          <XIcon size={20} aria-hidden="true" data-id="element-139" data-mp="208th93ra9d0t" />
        </button>
        <h2 id="sample-dialog-title" className="sr-only" data-id="element-140" data-mp="1w92h49fv9kjm">
          Workshop guide preview: {active.label}
        </h2>
        <div className="dialog-body" data-id="element-141" data-mp="2a79773ekqg8q">
          <svg className="real-pdf-page real-pdf-page--large" viewBox="0 0 1125 1910" role="img" aria-label={active.alt} data-id="element-142" data-mp="2a67s8nuft9lq"><image href={active.src} width="1125" height="2000" data-id="element-143" data-mp="257ehh36jbwp9" /></svg>
        </div>
        <button type="button" className="dialog-done" onClick={closeDialog} data-id="element-144" data-mp="1hhk3giahc23f">
          Done
        </button>
      </dialog>
    </section>;
}